import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * True only when a reveal can actually run: an observer exists and the user has
 * not asked for reduced motion.
 *
 * Evaluated synchronously during the first render on purpose. If the start
 * state were applied from an effect instead, the element would paint at full
 * opacity, then paint hidden, then animate in — a visible flash on every mount,
 * worst on dense pages like a member profile. Deciding before the first paint
 * means an element either animates cleanly or never hides at all.
 */
function canAnimate() {
  if (typeof window === 'undefined') return false;
  if (typeof IntersectionObserver === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') return false;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Observes an element once and reports when it first enters the viewport.
 *
 * `ready` gates the CSS start state, so a client that cannot animate renders
 * the finished content instead of an empty page.
 */
export default function useInView({ rootMargin = '0px 0px -10% 0px', threshold = 0 } = {}) {
  const ref = useRef(null);
  const [ready] = useState(canAnimate);
  const [inView, setInView] = useState(() => !canAnimate());

  // Release anything already on screen before the browser paints, so
  // above-the-fold content transitions out of its start state on the first
  // frame instead of blinking through it.
  useLayoutEffect(() => {
    if (!ready || inView) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    const box = node.getBoundingClientRect();
    const onScreen = box.height > 0 && box.bottom > 0 && box.top < window.innerHeight;
    if (!onScreen) return undefined;

    // Two frames: the first guarantees the start state is committed, the second
    // flips the target so the transition actually interpolates.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setInView(true));
    });

    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [ready, inView]);

  useEffect(() => {
    if (!ready || inView) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setInView(true);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin, threshold }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [ready, inView, rootMargin, threshold]);

  return { ref, ready, inView };
}
